import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Heart, Star, ShoppingCart, ArrowLeft, Share2, Search, Tag } from 'lucide-react';
import { fetchAllProducts } from '../db/firebaseProducts';
import { addToCart } from './Cart';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../db/firebase';

// Markdown Preview Component (same as in your other files)
const MarkdownPreview = ({ content }) => {
  const renderMarkdown = (text) => {
    if (!text) return <p className="text-gray-500 italic">No detailed description available.</p>;
    
    // Basic markdown parsing
    const html = text
      // Headers
      .replace(/^### (.*$)/gim, '<h3 class="text-lg font-semibold mt-4 mb-2">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="text-xl font-semibold mt-4 mb-2">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="text-2xl font-bold mt-4 mb-3">$1</h1>')
      // Bold
      .replace(/\*\*(.*?)\*\*/gim, '<strong class="font-bold">$1</strong>')
      // Italic
      .replace(/\*(.*?)\*/gim, '<em class="italic">$1</em>')
      // Underline
      .replace(/<u>(.*?)<\/u>/gim, '<u class="underline">$1</u>')
      // Bullet points
      .replace(/^- (.*$)/gim, '<li class="ml-4 list-disc">$1</li>')
      // Numbered lists
      .replace(/^\d+\. (.*$)/gim, '<li class="ml-4 list-decimal">$1</li>')
      // Line breaks
      .replace(/\n$/gim, '<br>')
      // Paragraphs
      .replace(/\n\n/gim, '</p><p class="my-2">')
      .replace(/([^\n])\n([^\n])/gim, '$1<br>$2');
    
    return `<p class="my-2">${html}</p>`;
  };

  return (
    <div 
      className="prose max-w-none"
      dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
    />
  );
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedImage, setSelectedImage] = useState('');
  const [currentStock, setCurrentStock] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });
  const [showCartSuccess, setShowCartSuccess] = useState(false);
  const [categories, setCategories] = useState([]);
  const [organizedCategories, setOrganizedCategories] = useState([]);
  const [selectedSoleType, setSelectedSoleType] = useState('Leather'); // Default sole type

  const imageRef = useRef(null);

  // Sole types configuration
  const soleTypes = [
    { name: 'Leather', priceAdjustment: 0 },
    { name: 'Vibrum', priceAdjustment: 0 },
    { name: 'Rubber', priceAdjustment: -1000 }
  ];

  // Function to organize categories into hierarchy
  const organizeCategoriesHierarchy = (categoriesData) => {
    const mainCategories = categoriesData.filter(cat => !cat.ParentCategory);
    const subCategories = categoriesData.filter(cat => cat.ParentCategory);

    const categoriesWithChildren = mainCategories.map(mainCat => ({
      ...mainCat,
      subcategories: subCategories.filter(subCat => subCat.ParentCategory === mainCat.id)
    }));

    return categoriesWithChildren;
  };

  // Fetch categories from Firebase
  const fetchCategories = async () => {
    try {
      const categoriesSnap = await getDocs(collection(db, "categories"));
      const categoriesData = categoriesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setCategories(categoriesData);
      
      // Organize categories into hierarchy
      const organized = organizeCategoriesHierarchy(categoriesData);
      setOrganizedCategories(organized);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  // Get category names from IDs
  const getCategoryNames = (categoryIds) => {
    if (!categoryIds || !Array.isArray(categoryIds) || categoryIds.length === 0) {
      return [];
    }

    const categoryNames = [];
    
    categoryIds.forEach(categoryId => {
      // Find main category
      const mainCategory = organizedCategories.find(cat => cat.id === categoryId);
      if (mainCategory) {
        categoryNames.push({
          id: mainCategory.id,
          name: mainCategory.CategoryName,
          type: 'main'
        });
      } else {
        // Find subcategory
        for (const mainCat of organizedCategories) {
          const subCategory = mainCat.subcategories?.find(sub => sub.id === categoryId);
          if (subCategory) {
            categoryNames.push({
              id: subCategory.id,
              name: subCategory.CategoryName,
              type: 'sub',
              parentId: mainCat.id,
              parentName: mainCat.CategoryName
            });
            break;
          }
        }
      }
    });

    return categoryNames;
  };

  // Group categories by main category
  const groupCategoriesByMain = (categoryNames) => {
    const grouped = {};
    
    categoryNames.forEach(category => {
      if (category.type === 'main') {
        if (!grouped[category.id]) {
          grouped[category.id] = {
            mainCategory: category,
            subcategories: []
          };
        }
      } else if (category.type === 'sub') {
        if (!grouped[category.parentId]) {
          grouped[category.parentId] = {
            mainCategory: {
              id: category.parentId,
              name: category.parentName,
              type: 'main'
            },
            subcategories: []
          };
        }
        grouped[category.parentId].subcategories.push(category);
      }
    });

    return Object.values(grouped);
  };

  // Fetch product data
  useEffect(() => {
    const loadProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch categories first
        await fetchCategories();
        
        // Then fetch product
        const products = await fetchAllProducts();
        const foundProduct = products.find(p => p.id === id);
        
        if (foundProduct) {
          setProduct(foundProduct);
          
          // Validate and set initial variant
          if (foundProduct.PType && foundProduct.PType.length > 0) {
            const initialVariant = foundProduct.PType[0];
            setSelectedVariant(initialVariant);
            setSelectedImage(initialVariant.PImgUrls?.[0] || '');
            
            // Validate and set initial size
            if (initialVariant.PStock && initialVariant.PStock.length > 0) {
              const firstAvailableSize = initialVariant.PStock.find(stock => stock.Qty > 0)?.Size || 
                                       initialVariant.PStock[0]?.Size || '';
              setSelectedSize(firstAvailableSize);
            } else {
              setSelectedSize('');
            }
            
            updateStockInfo(initialVariant, selectedSize);
          } else {
            setError('No variants available for this product');
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error loading product:', err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadProduct();
    }
  }, [id]);

  // Update stock information for selected size
  const updateStockInfo = (variant, size = '') => {
    if (!variant || !variant.PStock) {
      setCurrentStock(0);
      return;
    }

    if (size) {
      const sizeStock = variant.PStock.find(stock => stock.Size === size);
      setCurrentStock(sizeStock?.Qty || 0);
    } else {
      const totalStock = variant.PStock.reduce((total, stock) => total + (stock.Qty || 0), 0);
      setCurrentStock(totalStock);
    }
  };

  // Handle variant selection
  const handleVariantSelect = (variant) => {
    setSelectedVariant(variant);
    setSelectedImage(variant.PImgUrls?.[0] || '');
    setQuantity(1);
    
    // Find first available size for the new variant
    const firstAvailableSize = variant.PStock?.find(stock => stock.Qty > 0)?.Size || 
                             variant.PStock?.[0]?.Size || '';
    setSelectedSize(firstAvailableSize);
    
    // Update stock immediately
    updateStockInfo(variant, firstAvailableSize);
  };

  // Handle size selection
  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setQuantity(1);
    updateStockInfo(selectedVariant, size);
  };

  // Handle sole type selection
  const handleSoleTypeSelect = (soleType) => {
    setSelectedSoleType(soleType);
  };

  // Get unique color variants
  const getColorVariants = () => {
    if (!product?.PType) return [];
    
    const colorMap = new Map();
    
    product.PType.forEach(variant => {
      const color = variant.PSoleColor || variant.PTypeName || "Default";
      if (color && !colorMap.has(color)) {
        colorMap.set(color, variant);
      }
    });
    
    return Array.from(colorMap.entries()).map(([color, variant]) => ({
      color,
      variant
    }));
  };

  // Get available sizes for selected variant
  const getAvailableSizes = () => {
    if (!selectedVariant?.PStock) return [];
    
    return selectedVariant.PStock
      .filter(stock => stock.Qty > 0)
      .map(stock => stock.Size)
      .filter(Boolean)
      .sort((a, b) => {
        // Sort sizes numerically if they're numbers, otherwise alphabetically
        if (!isNaN(a) && !isNaN(b)) return parseInt(a) - parseInt(b);
        return a.localeCompare(b);
      });
  };

  // Calculate price and discount with sole type adjustment
  const getDisplayPrice = () => {
    if (!selectedVariant) return { price: 0, originalPrice: 0, discount: 0, soleAdjustment: 0 };
    
    const price = selectedVariant.PPrice || 0;
    const discountPrice = selectedVariant.PInOffer ? (selectedVariant.PDiscountPrice || 0) : null;
    
    // Get selected sole type adjustment
    const selectedSole = soleTypes.find(sole => sole.name === selectedSoleType);
    const soleAdjustment = selectedSole ? selectedSole.priceAdjustment : 0;
    
    let finalPrice = price;
    let finalDiscountPrice = discountPrice;

    // Apply sole adjustment to both original and discount prices
    if (discountPrice && discountPrice < price && discountPrice > 0) {
      finalDiscountPrice = Math.max(0, discountPrice + soleAdjustment);
      finalPrice = Math.max(0, price + soleAdjustment);
      const discount = Math.round(((finalPrice - finalDiscountPrice) / finalPrice) * 100);
      
      return {
        price: finalDiscountPrice,
        originalPrice: finalPrice,
        discount: discount,
        soleAdjustment: soleAdjustment
      };
    }
    
    finalPrice = Math.max(0, price + soleAdjustment);
    
    return {
      price: finalPrice > 0 ? finalPrice : 0,
      originalPrice: null,
      discount: 0,
      soleAdjustment: soleAdjustment
    };
  };

  // Image zoom handlers
  const handleMouseMove = (e) => {
    if (!imageRef.current) return;

    const { left, top, width, height } = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;

    setZoomPosition({ x, y });
  };

  const handleMouseEnter = () => setIsZoomed(true);
  const handleMouseLeave = () => setIsZoomed(false);

  // Handle quantity changes
  const handleQuantityChange = (change) => {
    const newQuantity = quantity + change;
    if (newQuantity >= 1 && newQuantity <= currentStock) {
      setQuantity(newQuantity);
    }
  };

  // Add to cart function
  const handleAddToCart = async () => {
    console.log('=== ADD TO CART CLICKED ===');
    console.log('Current stock:', currentStock);
    console.log('Selected variant:', selectedVariant);
    console.log('Selected size:', selectedSize);
    console.log('Selected sole type:', selectedSoleType);
    console.log('Quantity:', quantity);
    
    // Validate all required fields
    if (!product || !selectedVariant || !selectedSize) {
      console.error('Missing required product data');
      return;
    }

    if (currentStock <= 0) {
      console.error('Product is out of stock');
      return;
    }

    if (quantity > currentStock) {
      console.error('Quantity exceeds available stock');
      return;
    }

    try {
      console.log('All conditions met, adding to cart...');
      
      // Use the imported addToCart function
      const cartItem = {
        productId: product.id,
        variant: selectedVariant,
        size: selectedSize,
        soleType: selectedSoleType,
        quantity: quantity,
        product: product
      };

      console.log('Cart item data:', cartItem);
      
      const result = addToCart(cartItem);
      
      console.log('Cart function returned:', result);
      
      // Show success message
      setShowCartSuccess(true);
      setTimeout(() => setShowCartSuccess(false), 3000);
      
      // Force refresh cart by manually triggering event
      window.dispatchEvent(new Event('cartUpdated'));
      
    } catch (error) {
      console.error('Error adding to cart:', error);
    }
  };

  // View cart function
  const handleViewCart = () => {
    navigate('/cart');
  };

  // Format price with commas
  const formatPrice = (price) => {
    return price?.toLocaleString() || '0';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-beige p-4">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="h-96 bg-gray-300 rounded"></div>
              <div className="space-y-3">
                <div className="h-6 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                <div className="h-16 bg-gray-300 rounded"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-beige p-4 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-charcoal mb-3">{error || 'Product not found'}</h2>
          <button
            onClick={() => navigate('/shop')}
            className="bg-primary text-offwhite px-4 py-2 rounded hover:bg-primary-dark transition-colors text-sm"
          >
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  const priceInfo = getDisplayPrice();
  const colorVariants = getColorVariants();
  const availableSizes = getAvailableSizes();
  const productName = product.name || product.PName || "Unnamed Product";
  const productDescription = product.PDesc || "";
  
  // Get and organize categories
  const categoryNames = getCategoryNames(product.PCategories);
  const groupedCategories = groupCategoriesByMain(categoryNames);

  return (
    <div className="min-h-screen bg-beige">
      <div className='p-5'> 
        `
      </div>
      {/* Success Message */}
      {showCartSuccess && (
        <div className="fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2 animate-slide-in">
          <ShoppingCart size={20} />
          <span>Product added to cart successfully!</span>
          <button 
            onClick={handleViewCart}
            className="ml-2 bg-white text-green-600 px-3 py-1 rounded text-sm font-semibold hover:bg-green-50 transition-colors"
          >
            View Cart
          </button>
        </div>
      )}

      {/* Compact Navigation */}
      <div className="border-b border-taupe/30">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-charcoal hover:text-primary transition-colors text-sm"
          >
            <ArrowLeft size={16} className="mr-1" />
            Back
          </button>
        </div>
      </div>

      {/* Product Details */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Image Gallery - Desktop: Left, Mobile: Top */}
          <div className="space-y-4">
            {/* Main Image */}
            <div 
              className="relative bg-offwhite rounded-lg overflow-hidden border border-taupe/50 cursor-zoom-in"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onMouseMove={handleMouseMove}
              ref={imageRef}
            >
              <img
                src={selectedImage}
                alt={productName}
                className="w-full h-96 object-contain object-center"
                onError={(e) => {
                  e.target.src = '/placeholder-image.jpg';
                }}
              />
              
              {/* Zoom Overlay */}
              {isZoomed && (
                <div
                  className="absolute inset-0 overflow-hidden"
                  style={{
                    backgroundImage: `url(${selectedImage})`,
                    backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                    backgroundSize: '200%',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              )}

              {/* Compact Zoom Indicator */}
              <div className="absolute bottom-2 right-2 bg-black/60 text-offwhite px-2 py-1 rounded text-xs flex items-center space-x-1">
                <Search size={12} />
                <span>Zoom</span>
              </div>
            </div>

            {/* Thumbnails */}
            {selectedVariant?.PImgUrls && selectedVariant.PImgUrls.length > 1 && (
              <div className="flex space-x-2 overflow-x-auto pb-1">
                {selectedVariant.PImgUrls.map((imgUrl, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(imgUrl)}
                    className={`flex-shrink-0 w-16 h-16 border rounded overflow-hidden ${
                      selectedImage === imgUrl ? 'border-primary' : 'border-taupe/50'
                    }`}
                  >
                    <img
                      src={imgUrl}
                      alt={`${productName} view ${index + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = '/placeholder-image.jpg';
                      }}
                    />
                  </button>
                ))}
              </div>
            )}

            {/* Detailed Description - Desktop: Below Image, Mobile: Hidden (will be shown at end) */}
            <div className="hidden lg:block">
              {product.PDetailedDesc && (
                <div className=" rounded-lg border border-taupe/50 p-6 mt-4">
                  <h3 className="text-lg font-semibold text-charcoal mb-4">Product Details</h3>
                  <div className="prose max-w-none text-charcoal/70">
                    <MarkdownPreview content={product.PDetailedDesc} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Product Info - Desktop: Right, Mobile: Below Image */}
          <div className="space-y-4">
            {/* Header */}
            <div>
              {/* Categories Display */}
              <div className="flex justify-between items-start mb-2">
                {groupedCategories.length > 0 && (
                <div className="mb-2">
                  <div className="flex flex-wrap gap-1">
                    {groupedCategories.map((group, index) => (
                      <div key={group.mainCategory.id} className="flex items-center text-xs">
                        {index > 0 && <span className="text-charcoal/40 mx-1">•</span>}
                        <span className="text-xs text-charcoal/60 uppercase tracking-wide">{group.mainCategory.name}</span>
                        {group.subcategories.length > 0 && (
                          <>
                            <span className="text-xs text-charcoal/60 uppercase tracking-wide">→</span>
                            <span className="text-xs text-charcoal/60 uppercase tracking-wide0">
                              {group.subcategories.map(sub => sub.name).join(', ')}
                            </span>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
                <div className="flex space-x-1">
                  <button className="p-1.5 text-charcoal/70 hover:text-redburnt transition-colors">
                    <Share2 size={16} />
                  </button>
                  <button className="p-1.5 text-charcoal/70 hover:text-redburnt transition-colors">
                    <Heart size={16} />
                  </button>
                </div>
              </div>

              <h1 className="text-xl font-bold text-charcoal mb-2">{productName}</h1>

              {/* Rating */}
              {product.PRating > 0 && (
                <div className="flex items-center space-x-2 mb-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map(i => (
                      <Star
                        key={i}
                        size={14}
                        fill={i <= Math.floor(product.PRating) ? "#8B4513" : "currentColor"}
                        stroke="#D2B48C"
                        strokeWidth={1}
                        className={i <= Math.floor(product.PRating) ? "text-darktaupe" : "text-tan"}
                      />
                    ))}
                  </div>
                  <span className="text-xs text-charcoal/60">
                    {product.PRating % 1 === 0 ? product.PRating.toString() : product.PRating.toFixed(1)}
                  </span>
                </div>
              )}
            </div>

            {/* Price */}
            <div className="flex items-center space-x-3">
              <span className="text-xl font-bold text-primary">PKR {formatPrice(priceInfo.price)}</span>
              {priceInfo.originalPrice && priceInfo.originalPrice > priceInfo.price && (
                <>
                  <span className="text-sm text-charcoal/50 line-through">PKR {formatPrice(priceInfo.originalPrice)}</span>
                  <span className="bg-redburnt/10 text-redburnt px-1.5 py-0.5 rounded-full text-xs font-semibold">
                    -{priceInfo.discount}%
                  </span>
                </>
              )}
              {/* Show sole adjustment if applicable */}
              {priceInfo.soleAdjustment !== 0 && (
                <span className={`text-xs ${priceInfo.soleAdjustment < 0 ? 'text-green-600' : 'text-redburnt'}`}>
                  {priceInfo.soleAdjustment > 0 ? '+' : ''}{priceInfo.soleAdjustment} PKR
                </span>
              )}
            </div>

            {/* Description */}
            {productDescription && (
              <div>
                <p className="text-sm text-charcoal/70 leading-relaxed line-clamp-3">{productDescription}</p>
              </div>
            )}

            {/* Color Variants */}
            {colorVariants.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-charcoal mb-2">Color</h3>
                <div className="flex flex-wrap gap-2">
                  {colorVariants.map(({ color, variant }, index) => (
                    <button
                      key={index}
                      onClick={() => handleVariantSelect(variant)}
                      className={`flex items-center space-x-1.5 px-2.5 py-1.5 border rounded text-xs transition-all ${
                        selectedVariant === variant ? 'border-primary bg-primary/10' : 'border-taupe/50'
                      }`}
                    >
                      <div
                        className="w-3 h-3 rounded-full border border-offwhite shadow-sm"
                        style={{
                          backgroundColor: 
                            color.toLowerCase() === 'red' ? '#ce1c1cff' : 
                            color.toLowerCase() === 'blue' ? '#0b3e91ff' :
                            color.toLowerCase() === 'green' ? '#10b981' :
                            color.toLowerCase() === 'black' ? '#000000' :
                            color.toLowerCase() === 'white' ? '#ffffff' :
                            color.toLowerCase() === 'brown' ? '#a54d06ff' :
                            color.toLowerCase() === 'tan' ? '#cc9c5eff' :
                            color.toLowerCase() === 'navy' ? '#000080' : '#d1d5db'
                        }}
                      />
                      <span className="capitalize">{color}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Sole Type Selection */}
            <div>
              <h3 className="text-sm font-semibold text-charcoal mb-2">Sole Type</h3>
              <select
                value={selectedSoleType}
                onChange={(e) => handleSoleTypeSelect(e.target.value)}
                className="w-full px-3 py-2 border border-taupe/50 rounded text-sm focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {soleTypes.map((soleType, index) => (
                  <option key={index} value={soleType.name}>
                    {soleType.name} 
                    {/* {soleType.priceAdjustment !== 0 ? 
                      `(${soleType.priceAdjustment > 0 ? '+' : ''}${soleType.priceAdjustment} PKR)` : 
                      ''} */}
                  </option>
                ))}
              </select>
            </div>

            {/* Size Selection */}
            {availableSizes.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-charcoal mb-2">Size</h3>
                <div className="flex flex-wrap gap-1.5">
                  {availableSizes.map((size, index) => (
                    <button
                      key={index}
                      onClick={() => handleSizeSelect(size)}
                      className={`px-3 py-1.5 border rounded text-sm font-medium transition-all ${
                        selectedSize === size
                          ? 'border-primary bg-primary text-offwhite'
                          : 'border-taupe/50 text-charcoal hover:border-primary'
                      }`}
                    >
                      {size}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Stock Status */}
            <div className="text-sm">
              {currentStock > 0 ? (
                <span className="text-green-600 font-medium">{currentStock} in stock</span>
              ) : (
                <span className="text-redburnt font-medium">Out of stock</span>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center space-x-3">
                <span className="text-sm font-semibold text-charcoal">Quantity:</span>
                <div className="flex items-center border border-taupe/50 rounded">
                  <button
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 1}
                    className="px-3 py-1.5 text-charcoal hover:bg-taupe/20 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    -
                  </button>
                  <span className="px-3 py-1.5 text-charcoal font-semibold text-sm min-w-8 text-center">{quantity}</span>
                  <button
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= currentStock}
                    className="px-3 py-1.5 text-charcoal hover:bg-taupe/20 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    +
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <button
                  onClick={handleAddToCart}
                  disabled={currentStock === 0 || !selectedSize}
                  className={`w-full flex items-center justify-center space-x-2 py-3 px-4 rounded font-semibold text-sm transition-colors ${
                    currentStock > 0 && selectedSize
                      ? 'bg-primary text-offwhite hover:bg-primary-dark'
                      : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                  }`}
                >
                  <ShoppingCart size={16} />
                  <span>
                    {currentStock > 0 && selectedSize
                      ? `Add to Cart - PKR ${formatPrice(priceInfo.price * quantity)}`
                      : !selectedSize
                      ? 'Select Size'
                      : 'Out of Stock'}
                  </span>
                </button>
              </div>
            </div>

            {/* Compact Additional Info */}
            <div className="border-t border-taupe/30 pt-3">
              <div className="grid grid-cols-2 gap-2 text-xs text-charcoal/60">
                <div><span className="font-medium">SKU:</span> {product.id}</div>
                <div>
                  <span className="font-medium">Stock:</span> 
                  <span className={currentStock > 0 ? "text-green-600 ml-1" : "text-redburnt ml-1"}>
                    {currentStock}
                  </span>
                </div>
                {selectedVariant?.PSoleColor && (
                  <div><span className="font-medium">Color:</span> {selectedVariant.PSoleColor}</div>
                )}
                {selectedSize && (
                  <div><span className="font-medium">Size:</span> {selectedSize}</div>
                )}
                <div><span className="font-medium">Sole Type:</span> {selectedSoleType}</div>
                
                {/* Categories in additional info */}
                {groupedCategories.length > 0 && (
                  <div className="col-span-2 mt-1">
                    <div className="flex items-center space-x-1 text-xs">
                      <Tag size={10} className="text-charcoal/40" />
                      <span className="font-medium text-charcoal/70">Categories:</span>
                      <div className="flex flex-wrap gap-1">
                        {groupedCategories.map((group, index) => (
                          <span key={group.mainCategory.id} className="text-charcoal/60">
                            {group.mainCategory.name}
                            {group.subcategories.length > 0 && ` (${group.subcategories.map(sub => sub.name).join(', ')})`}
                            {index < groupedCategories.length - 1 && ','}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Description - Mobile: At the end, below everything */}
        <div className="lg:hidden mt-8">
          {product.PDetailedDesc && (
            <div className=" rounded-lg border border-taupe/50 p-6">
              <h3 className="text-lg font-semibold text-charcoal mb-4">Product Details</h3>
              <div className="prose max-w-none text-charcoal/70">
                <MarkdownPreview content={product.PDetailedDesc} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;